import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, CreditCard, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useRecaptcha } from '@/contexts/RecaptchaContext';
import { buildApiUrl } from '@/config/env';

interface PaddleInlineCheckoutProps {
  videoId: string;
  jobId: string;
  amount: number;
  isOpen: boolean;
  onClose: () => void;
  onPaymentComplete: (paymentId: string) => void;
  onError: (error: string) => void;
}

declare global {
  interface Window {
    Paddle: any;
    __paddle_inited?: boolean;
  }
}

const PaddleInlineCheckout: React.FC<PaddleInlineCheckoutProps> = ({
  videoId,
  jobId,
  amount,
  isOpen,
  onClose,
  onPaymentComplete,
  onError
}) => {
  const [isPaddleLoaded, setIsPaddleLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [hasCreatedCheckout, setHasCreatedCheckout] = useState(false);
  
  // Use ref to track actual checkoutId value
  const checkoutIdRef = useRef<string | null>(null);
  
  // 💡 Gợi ý: Có thể tách thành 2 state riêng biệt để UI rõ ràng hơn:
  // - isCreatingCheckout: khi gọi API tạo checkout
  // - isProcessingPayment: khi user bấm Pay button
  // const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  // const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  useEffect(() => {
    if (isOpen && !isPaddleLoaded) {
      // Load Paddle.js v2 script
      const script = document.createElement('script');
      script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
      script.async = true;
      script.onload = () => {
        console.log('📜 Paddle.js v2 script loaded successfully');
        
        if (window.Paddle) {
          console.log('✅ Paddle object available in window');
          
          // Set environment before Initialize (if sandbox)
          if (import.meta.env.VITE_PADDLE_ENVIRONMENT === 'sandbox') {
            console.log('🌍 Setting Paddle environment to sandbox');
            window.Paddle.Environment.set('sandbox');
          }
          
          const clientToken = import.meta.env.VITE_PADDLE_CLIENT_TOKEN;
          console.log('🔑 Client Token from env:', clientToken ? `${clientToken.substring(0, 10)}...` : 'NOT SET');
          
          if (!clientToken) {
            console.error('❌ Missing Paddle client token:', import.meta.env.VITE_PADDLE_CLIENT_TOKEN);
            onError('Missing Paddle configuration');
            return;
          }
          
          // Tránh Initialize 2 lần
          if (!window.__paddle_inited) {
            console.log('⚙️ Initializing Paddle Billing v2...');
            window.Paddle.Initialize({
              token: clientToken,
              checkout: {
                settings: {
                  displayMode: 'inline',
                  frameTarget: 'paddle-checkout',
                  frameInitialHeight: '450',
                  frameStyle: 'width:100%;min-width:312px;background:transparent;border:none;'
                }
              },
              eventCallback: (e: any) => {
                console.log('📨 Paddle event received:', {
                  name: e.name,
                  data: e.data,
                  timestamp: new Date().toISOString()
                });
                
                if (e.name === 'checkout.completed') {
                  const transactionId = e.data?.transaction_id || e.transaction_id;
                  console.log('✅ Payment completed, transaction ID:', transactionId);
                  
                  // Call payment complete callback
                  onPaymentComplete(transactionId || '');
                  
                  // Close dialog
                  onClose();
                  
                  console.log('🎯 Payment flow completed successfully');
                } else if (e.name === 'checkout.closed') {
                  console.log('❌ Checkout closed by user');
                } else if (e.name === 'checkout.error') {
                  console.error('❌ Checkout error:', e);
                } else {
                  console.log('📝 Other Paddle event:', e.name);
                }
              }
            });
            window.__paddle_inited = true;
            console.log('✅ Paddle Billing v2 initialized');
          } else {
            console.log('⚠️ Paddle already initialized, skipping...');
          }
          
          // Log để chắc chắn đang dùng lib v2
          console.log('Paddle lib version:', window.Paddle?.Status?.libraryVersion);
          
          setIsPaddleLoaded(true);
        } else {
          console.error('❌ Paddle object not found in window after script load');
        }
      };
      script.onerror = () => {
        onError('Failed to load Paddle payment system');
      };
      document.head.appendChild(script);

      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    }
  }, [isOpen, isPaddleLoaded, onError, videoId]);

  useEffect(() => {
    console.log('🔄 useEffect triggered:', {
      isOpen,
      isPaddleLoaded,
      checkoutId,
      hasCreatedCheckout,
      timestamp: new Date().toISOString()
    });
    
    if (isOpen && isPaddleLoaded && !checkoutId && !hasCreatedCheckout) {
      console.log('🚀 Creating checkout session...');
      createCheckoutSession();
    }
  }, [isOpen, isPaddleLoaded, checkoutId, hasCreatedCheckout]);

  // Debug: Log every checkoutId change and update ref
  useEffect(() => {
    console.log('🆔 Checkout ID changed:', {
      from: checkoutIdRef.current,
      to: checkoutId,
      timestamp: new Date().toISOString()
    });
    
    // Update ref with new value
    checkoutIdRef.current = checkoutId;
  }, [checkoutId]);

  // Debug: Log component re-renders
  useEffect(() => {
    console.log('🔄 Component re-render:', {
      isOpen,
      isPaddleLoaded,
      checkoutId,
      checkoutIdRef: checkoutIdRef.current,
      hasCreatedCheckout,
      timestamp: new Date().toISOString()
    });
  });

  const createCheckoutSession = async () => {
    // Prevent multiple calls
    if (checkoutId) {
      console.log('⚠️ Checkout session already exists, skipping creation');
      return;
    }
    
    try {
      console.log('🚀 Starting checkout session creation...');
      setIsProcessing(true);
      
      const response = await fetch(buildApiUrl('/api/paddle/create-checkout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId,
          jobId,
          amount,
          currency: 'USD',
          productName: 'Video Subtitle Generation',
          customerEmail: '',
          returnUrl: `${window.location.origin}/payment-success?videoId=${videoId}`,
          cancelUrl: `${window.location.origin}/payment-cancelled?videoId=${videoId}`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const responseData = await response.json();
      console.log('🔍 Paddle API Response:', responseData);
      
      const { checkoutId: newCheckoutId } = responseData;
      console.log('📝 Extracted checkout ID:', newCheckoutId);
      
      if (!newCheckoutId) {
        console.error('❌ No checkout ID in response:', responseData);
        throw new Error('Invalid checkout response');
      }
      
      console.log('🔄 Setting checkout ID from:', checkoutId, 'to:', newCheckoutId);
      setCheckoutId(newCheckoutId);
      setHasCreatedCheckout(true);
      console.log('✅ Checkout ID set successfully:', newCheckoutId);
      
      // Verify the state was set - use the new value directly
      setTimeout(() => {
        console.log('🔍 Checkout ID verification:', {
          expected: newCheckoutId,
          current: checkoutId, // This will still be old value due to closure
          refValue: checkoutIdRef.current, // This should have the new value
          timestamp: new Date().toISOString()
        });
      }, 100);

    } catch (error) {
      console.error('Checkout creation failed:', error);
      onError('Failed to create checkout session');
    } finally {
      // 🔧 Quan trọng: tắt trạng thái processing sau khi xong (thành công hoặc lỗi)
      setIsProcessing(false);
      console.log('✅ Processing state reset to false');
    }
  };

  const openInlineCheckout = () => {
    if (!checkoutId || !isPaddleLoaded) {
      onError('Checkout not ready');
      return;
    }

    try {
      console.log('🚀 Opening Paddle Checkout v2 with product ID:', checkoutId);
      
      // Use Paddle Billing v2 checkout with transactionId
      console.log('🚀 Opening Paddle Billing v2 checkout with transaction ID:', checkoutId);
      
      try {
        // Use Paddle Billing v2 Checkout.open with transactionId
        window.Paddle.Checkout.open({
          transactionId: checkoutId // Use the transaction ID
        });
        
        console.log('✅ Paddle checkout opened successfully');
      } catch (error) {
        console.error('❌ Paddle checkout failed:', error);
        onError('Checkout failed. Please try again.');
      }
    } catch (error) {
      console.error('❌ Inline checkout failed:', error);
      onError('Inline checkout failed. Please try again.');
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Complete Payment</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              disabled={isProcessing}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              ${amount.toFixed(2)}
            </div>
            <p className="text-sm text-gray-600">
              Video Subtitle Generation
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-2">What you'll get:</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• High-quality AI-generated subtitles</li>
              <li>• Multiple language support</li>
              <li>• Fast processing (depends on video length)</li>
              <li>• Secure payment processing</li>
            </ul>
          </div>

          {/* Paddle Checkout Container */}
          <div className="paddle-checkout" />



          {!isPaddleLoaded ? (
            <Button disabled className="w-full">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading Payment System... {!isPaddleLoaded && '(Paddle.js not loaded)'}
            </Button>
          ) : !checkoutId ? (
            <Button disabled className="w-full">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Preparing Checkout... {!checkoutId && '(No checkout ID)'}
            </Button>
          ) : (
            <Button
              onClick={openInlineCheckout}
              disabled={isProcessing}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pay ${amount.toFixed(2)} Securely
                </>
              )}
            </Button>
          )}



          <div className="text-xs text-gray-500 text-center">
            <p>Your payment is processed securely by Paddle</p>
            <p>No credit card information is stored on our servers</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaddleInlineCheckout;
